import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"

/**
 * Phase 2.8 Tier 1 + Tier 2 — Report Builder.
 *
 * Three-pane layout:
 *   - Left tray  ("availableTray")   — registry columns grouped by category
 *                                      + linked-items sub-tray (Tier 2)
 *                                      drag source via SortableJS
 *   - Center canvas ("canvas")       — selected columns in display order
 *   - Right rail ("filtersRail")     — filters (with Salesforce-style
 *                                      filter logic), sort, group +
 *                                      aggregate (up to 3 levels),
 *                                      buckets, formula columns,
 *                                      chart blocks (Tier 2)
 *
 * Live preview re-renders 25 rows on debounced 600ms after any change.
 */
export default class extends Controller {
  static targets = [
    "datasetSelect",
    "portfolioToggle",
    "availableTray",
    "linkedTray",
    "columnSearch",
    "canvas",
    "canvasEmpty",
    "descriptionWrapper",
    "descriptionToggle",
    "filtersList",
    "filtersBadge",
    "filterLogicInput",
    "filterLogicError",
    "filterLogicMode",
    "sortList",
    "sortBadge",
    "groupList",
    "groupBadge",
    "aggregateList",
    "chartList",
    "chartsBadge",
    "formulaList",
    "formulasBadge",
    "previewBody",
    "previewSummary",
    "chartPreviewArea",
    "columnKeysInput",
    "filtersInput",
    "sortByInput",
    "groupByKeysInput",
    "aggregatesInput",
    "formulaColumnsInput",
    "chartBlocksInput",
    "datasetKeyInput"
  ]

  static values = {
    builderUrl: String,
    previewUrl: String,
    initialDefinition: Object,
    reportId: { type: Number, default: 0 }
  }

  connect() {
    this._datasets = []
    this._currentDataset = null
    this._definition = this._normalizeDefinition(this.initialDefinitionValue || {})
    this._previewTimer = null
    this._collapsed = this._loadCollapsedState()
    this._charts = {}

    this._loadRegistry().then(() => {
      this._renderAll()
      this._initSortables()
      this._writeHiddenInputs()
      this._schedulePreview(0)
    })

    this.element.addEventListener("formula-column:added", (e) => this._onFormulaAdded(e))
    this.element.addEventListener("filter-logic:changed", (e) => this._onFilterLogicChanged(e))
    document.addEventListener("keydown", this._onKey = this._onKey.bind(this))
  }

  disconnect() {
    if (this._previewTimer) clearTimeout(this._previewTimer)
    document.removeEventListener("keydown", this._onKey)
    Object.values(this._charts).forEach(c => c?.destroy && c.destroy())
    this._charts = {}
  }

  _onKey(event) {
    const meta = event.metaKey || event.ctrlKey
    if (meta && event.key.toLowerCase() === "s") {
      event.preventDefault()
      this.element.requestSubmit?.()
    } else if (meta && event.key === "Enter") {
      event.preventDefault()
      this._schedulePreview(0)
    } else if (event.key === "Escape") {
      const dlg = document.querySelector("[data-report-formula-column-target='modal']:not(.hidden)")
      if (dlg) dlg.classList.add("hidden")
    }
  }

  // ── Dataset selection ───────────────────────────────────────────

  changeDataset(event) {
    const key = event.currentTarget.value
    this._currentDataset = this._findDataset(key)
    this._definition.dataset_key = key
    this._definition.column_keys = []
    this._definition.filters = []
    this._definition.sort_by = []
    this._definition.group_by_keys = []
    this._definition.aggregates = {}
    this._definition.formula_columns = []
    this._definition.chart_blocks = []
    this._definition.filter_logic = ""
    this._renderAll()
    this._initSortables()
    this._writeHiddenInputs()
    this._schedulePreview(0)
  }

  togglePortfolio(event) {
    const wantsPortfolio = !!event.currentTarget.checked
    this._renderDatasetOptions(wantsPortfolio)
    if (this.hasDatasetSelectTarget) {
      this._currentDataset = this._findDataset(this.datasetSelectTarget.value)
      this._definition.dataset_key = this.datasetSelectTarget.value
      this._renderAll()
      this._writeHiddenInputs()
      this._schedulePreview(0)
    }
  }

  // ── UI affordances ───────────────────────────────────────────────

  toggleDescription() {
    if (!this.hasDescriptionWrapperTarget) return
    const hidden = this.descriptionWrapperTarget.classList.toggle("hidden")
    if (this.hasDescriptionToggleTarget) {
      this.descriptionToggleTarget.textContent = hidden ? "+ Description" : "− Description"
    }
    if (!hidden) {
      const ta = this.descriptionWrapperTarget.querySelector("textarea")
      if (ta) ta.focus()
    }
  }

  filterColumnSearch() {
    this._renderTray()
    this._renderLinkedTray()
    this._initSortables()
  }

  // ── Column actions ──────────────────────────────────────────────

  addColumn(event) {
    const key = event.currentTarget.dataset.columnKey
    if (!this._definition.column_keys.includes(key)) {
      this._definition.column_keys.push(key)
    }
    this._renderCanvas(); this._renderTray(); this._renderLinkedTray()
    this._initSortables()
    this._writeHiddenInputs()
    this._schedulePreview()
  }

  removeColumn(event) {
    const key = event.currentTarget.dataset.columnKey
    this._definition.column_keys = this._definition.column_keys.filter(c => c !== key)
    this._renderCanvas(); this._renderTray(); this._renderLinkedTray()
    this._initSortables()
    this._writeHiddenInputs()
    this._schedulePreview()
  }

  // ── Filter actions ──────────────────────────────────────────────

  addFilter() {
    if (!this._currentDataset) return
    const firstFilterable = this._currentDataset.columns.find(c => c.filterable)
    if (!firstFilterable) return
    this._definition.filters.push({
      column_key: firstFilterable.key, operator: "eq", value: "",
      sequence: this._definition.filters.length + 1
    })
    this._renderFilters()
    this._writeHiddenInputs()
    this._schedulePreview()
  }

  removeFilter(event) {
    const idx = parseInt(event.currentTarget.dataset.filterIndex, 10)
    this._definition.filters.splice(idx, 1)
    this._definition.filters.forEach((f, i) => { f.sequence = i + 1 })
    this._renderFilters()
    this._writeHiddenInputs()
    this._schedulePreview()
  }

  updateFilterColumn(event) {
    const idx = parseInt(event.currentTarget.dataset.filterIndex, 10)
    const newKey = event.currentTarget.value
    const newCol = this._lookupColumn(newKey)
    const validOps = this._operatorsFor(newCol?.data_type || "string")
    const filter = this._definition.filters[idx]
    filter.column_key = newKey
    // Reset operator if the previous one isn't valid for the new column's
    // data type — keeps the dropdown coherent (no `>` on an enum, etc.).
    if (!validOps.includes(filter.operator)) {
      filter.operator = validOps[0]
    }
    // Clear value when switching to/from valueless operators or different
    // type — typed input would otherwise look stale.
    filter.value = ""
    this._renderFilters()
    this._writeHiddenInputs()
    this._schedulePreview()
  }

  updateFilterOperator(event) {
    const idx = parseInt(event.currentTarget.dataset.filterIndex, 10)
    const op = event.currentTarget.value
    this._definition.filters[idx].operator = op
    if (op === "is_null" || op === "is_not_null") {
      this._definition.filters[idx].value = ""
    }
    this._renderFilters()
    this._writeHiddenInputs()
    this._schedulePreview()
  }

  updateFilterValue(event) {
    const idx = parseInt(event.currentTarget.dataset.filterIndex, 10)
    this._definition.filters[idx].value = event.currentTarget.value
    this._writeHiddenInputs()
    this._schedulePreview()
  }

  _onFilterLogicChanged(event) {
    this._definition.filter_logic = (event.detail?.expression || "").trim()
    this._writeHiddenInputs()
    this._schedulePreview()
  }

  // ── Sort actions ────────────────────────────────────────────────

  addSort() {
    if (!this._currentDataset) return
    const firstSortable = this._currentDataset.columns.find(c => c.sortable)
    if (!firstSortable) return
    this._definition.sort_by.push({ column_key: firstSortable.key, direction: "asc" })
    this._renderSort()
    this._writeHiddenInputs()
    this._schedulePreview()
  }

  removeSort(event) {
    const idx = parseInt(event.currentTarget.dataset.sortIndex, 10)
    this._definition.sort_by.splice(idx, 1)
    this._renderSort()
    this._writeHiddenInputs()
    this._schedulePreview()
  }

  updateSortColumn(event) {
    const idx = parseInt(event.currentTarget.dataset.sortIndex, 10)
    this._definition.sort_by[idx].column_key = event.currentTarget.value
    this._writeHiddenInputs()
    this._schedulePreview()
  }

  updateSortDirection(event) {
    const idx = parseInt(event.currentTarget.dataset.sortIndex, 10)
    this._definition.sort_by[idx].direction = event.currentTarget.value
    this._writeHiddenInputs()
    this._schedulePreview()
  }

  // ── Group + Aggregate ───────────────────────────────────────────

  addGroup() {
    if (this._definition.group_by_keys.length >= 3) return
    const first = this._currentDataset?.columns.find(c => c.filterable)
    if (!first) return
    this._definition.group_by_keys.push(first.key)
    this._renderGroup()
    this._writeHiddenInputs()
    this._schedulePreview()
  }

  removeGroup(event) {
    const idx = parseInt(event.currentTarget.dataset.groupIndex, 10)
    this._definition.group_by_keys.splice(idx, 1)
    this._renderGroup()
    this._writeHiddenInputs()
    this._schedulePreview()
  }

  updateGroupColumn(event) {
    const idx = parseInt(event.currentTarget.dataset.groupIndex, 10)
    this._definition.group_by_keys[idx] = event.currentTarget.value
    this._writeHiddenInputs()
    this._schedulePreview()
  }

  addAggregate() {
    const first = this._availableAggregateColumns()[0]
    if (!first) return
    this._definition.aggregates[first.key] = "sum"
    this._renderAggregates()
    this._writeHiddenInputs()
    this._schedulePreview()
  }

  updateAggregate(event) {
    const colKey = event.currentTarget.dataset.aggregateColumnKey
    const fn = event.currentTarget.value
    if (!fn) {
      delete this._definition.aggregates[colKey]
    } else {
      this._definition.aggregates[colKey] = fn
    }
    this._writeHiddenInputs()
    this._schedulePreview()
  }

  removeAggregate(event) {
    const colKey = event.currentTarget.dataset.aggregateColumnKey
    delete this._definition.aggregates[colKey]
    this._renderAggregates()
    this._writeHiddenInputs()
    this._schedulePreview()
  }

  // ── Chart blocks ────────────────────────────────────────────────

  addChartBlock() {
    if ((this._definition.chart_blocks || []).length >= 4) return
    const first = this._currentDataset?.columns.find(c => c.filterable)
    if (!first) return
    const value = this._currentDataset?.columns.find(c => c.aggregatable) || first
    this._definition.chart_blocks.push({
      kind: "bar",
      group_by_key: first.key,
      value_key: value.key,
      value_aggregate: value.aggregatable ? "sum" : "count",
      stack_by_key: ""
    })
    this._renderCharts()
    this._writeHiddenInputs()
    this._schedulePreview()
  }

  removeChartBlock(event) {
    const idx = parseInt(event.currentTarget.dataset.chartIndex, 10)
    this._definition.chart_blocks.splice(idx, 1)
    this._renderCharts()
    this._writeHiddenInputs()
    this._schedulePreview()
  }

  updateChartField(event) {
    const idx = parseInt(event.currentTarget.dataset.chartIndex, 10)
    const field = event.currentTarget.dataset.field
    if (Number.isNaN(idx) || !field) return
    this._definition.chart_blocks[idx][field] = event.currentTarget.value
    this._writeHiddenInputs()
    this._schedulePreview()
  }

  // ── Formula / bucket columns ────────────────────────────────────

  _onFormulaAdded(event) {
    const spec = event.detail?.spec
    if (!spec || !spec.key) return
    const idx = this._definition.formula_columns.findIndex(f => f.key === spec.key)
    if (idx >= 0) {
      this._definition.formula_columns[idx] = spec
    } else {
      this._definition.formula_columns.push(spec)
    }
    this._renderFormulas(); this._renderTray()
    this._writeHiddenInputs()
    this._schedulePreview()
  }

  removeFormula(event) {
    const key = event.currentTarget.dataset.formulaKey
    this._definition.formula_columns = this._definition.formula_columns.filter(f => f.key !== key)
    this._renderFormulas(); this._renderTray()
    this._writeHiddenInputs()
    this._schedulePreview()
  }

  // ── Accordion ───────────────────────────────────────────────────

  toggleAccordion(event) {
    const section = event.currentTarget.dataset.section
    if (!section) return
    this._collapsed[section] = !this._collapsed[section]
    this._saveCollapsedState()
    this._applyAccordionState()
  }

  // ── Internal ────────────────────────────────────────────────────

  async _loadRegistry() {
    try {
      const resp = await fetch(this.builderUrlValue, { headers: { "Accept": "application/json" } })
      const json = await resp.json()
      this._datasets = json.datasets || []
      const wantsPortfolio = this._wantsPortfolio()
      const visible = this._datasets.filter(d => !!d.portfolio === !!wantsPortfolio)
      // Prefer the persisted/current dataset_key only if it survives the
      // portfolio filter; otherwise fall back to the first visible option
      // so the <select> never holds a key with no matching <option>.
      const persisted = this._definition.dataset_key
      const initialKey = (persisted && visible.some(d => d.key === persisted))
        ? persisted
        : visible[0]?.key
      this._renderDatasetOptions(wantsPortfolio, initialKey)
      this._currentDataset = this._findDataset(initialKey)
      this._definition.dataset_key = initialKey
      if (this.hasDatasetSelectTarget && initialKey) this.datasetSelectTarget.value = initialKey
    } catch (e) {
      console.warn("[report-builder] failed to load registry", e)
    }
  }

  _wantsPortfolio() {
    if (this.hasPortfolioToggleTarget) return this.portfolioToggleTarget.checked
    return !!this._definition.portfolio_scope
  }

  _renderDatasetOptions(wantsPortfolio, preferKey = null) {
    if (!this.hasDatasetSelectTarget) return
    const options = this._datasets
      .filter(d => !!d.portfolio === !!wantsPortfolio)
    this.datasetSelectTarget.innerHTML = options.map(d =>
      `<option value="${this._escapeAttr(d.key)}">${this._escapeHtml(d.label)}</option>`
    ).join("")
    if (options.length === 0) return
    const selected = (preferKey && options.some(d => d.key === preferKey))
      ? preferKey
      : options[0].key
    this.datasetSelectTarget.value = selected
  }

  _findDataset(key) {
    return this._datasets.find(d => d.key === key) || null
  }

  _normalizeDefinition(input) {
    return {
      dataset_key: input.dataset_key || "",
      column_keys: Array.isArray(input.column_keys) ? input.column_keys.slice() : [],
      filters: Array.isArray(input.filters) ? input.filters.map(f => Object.assign({}, f)) : [],
      sort_by: Array.isArray(input.sort_by) ? input.sort_by.map(s => Object.assign({}, s)) : [],
      group_by_keys: Array.isArray(input.group_by_keys) ? input.group_by_keys.slice() : [],
      aggregates: input.aggregates && typeof input.aggregates === "object" ? Object.assign({}, input.aggregates) : {},
      filter_logic: input.filter_logic || "",
      formula_columns: Array.isArray(input.formula_columns) ? input.formula_columns.slice() : [],
      chart_blocks: Array.isArray(input.chart_blocks) ? input.chart_blocks.slice() : [],
      portfolio_scope: !!input.portfolio_scope
    }
  }

  _renderAll() {
    this._renderTray()
    this._renderLinkedTray()
    this._renderCanvas()
    this._renderFilters()
    this._renderSort()
    this._renderGroup()
    this._renderAggregates()
    this._renderCharts()
    this._renderFormulas()
    this._applyAccordionState()
    this._updateBadges()
  }

  _updateBadges() {
    const set = (target, count) => {
      if (!target) return
      if (count > 0) {
        target.textContent = count
        target.classList.remove("hidden")
      } else {
        target.classList.add("hidden")
      }
    }
    if (this.hasFiltersBadgeTarget)  set(this.filtersBadgeTarget,  (this._definition.filters || []).length)
    if (this.hasSortBadgeTarget)     set(this.sortBadgeTarget,     (this._definition.sort_by || []).length)
    if (this.hasGroupBadgeTarget) {
      const grpCount = (this._definition.group_by_keys || []).length
      const aggCount = Object.keys(this._definition.aggregates || {}).length
      set(this.groupBadgeTarget, grpCount + aggCount)
    }
    if (this.hasFormulasBadgeTarget) set(this.formulasBadgeTarget, (this._definition.formula_columns || []).length)
    if (this.hasChartsBadgeTarget)   set(this.chartsBadgeTarget,   (this._definition.chart_blocks || []).length)
  }

  _renderTray() {
    if (!this.hasAvailableTrayTarget || !this._currentDataset) return
    const used = new Set(this._definition.column_keys)
    const q = (this.hasColumnSearchTarget ? this.columnSearchTarget.value : "").trim().toLowerCase()
    const matchesQuery = (col) =>
      !q || col.label.toLowerCase().includes(q) || (col.key || "").toLowerCase().includes(q)
    const buckets = { Basic: [], Schedule: [], Financial: [], Status: [] }
    this._currentDataset.columns.forEach(col => {
      if (used.has(col.key)) return
      if (!matchesQuery(col)) return
      const bucket = this._categoryFor(col.data_type)
      buckets[bucket].push(col)
    })

    let html = ""
    Object.keys(buckets).forEach(name => {
      const cols = buckets[name]
      if (cols.length === 0) return
      html += `<div class="mb-3">
        <div class="text-[11px] font-medium uppercase tracking-wide text-gray-500 px-2 py-1">${name}</div>
        <div class="space-y-1">`
      cols.forEach(col => {
        html += `<button type="button"
          class="w-full text-left text-[13px] px-2 py-1.5 rounded hover:bg-brand-50 hover:text-brand-700 cursor-grab active:cursor-grabbing flex items-center gap-2"
          data-action="click->report-builder#addColumn"
          data-tray-item="true"
          data-column-key="${this._escapeAttr(col.key)}">
          <span class="text-gray-300 text-[14px] leading-none" aria-hidden="true">⋮⋮</span>
          <span class="flex-1 truncate">${this._escapeHtml(col.label)}</span>
          <span class="text-[11px] text-gray-400 flex-shrink-0">${col.data_type}</span>
        </button>`
      })
      html += "</div></div>"
    })

    if ((this._definition.formula_columns || []).length > 0) {
      const usedSet = new Set(this._definition.column_keys)
      const virtuals = this._definition.formula_columns
        .filter(f => !usedSet.has(f.key))
        .filter(f => matchesQuery({ label: f.label || f.key, key: f.key }))
      if (virtuals.length) {
        html += `<div class="mb-3">
          <div class="text-[11px] font-medium uppercase tracking-wide text-gray-500 px-2 py-1">Formulas + Buckets</div>
          <div class="space-y-1">`
        virtuals.forEach(col => {
          html += `<button type="button"
            class="w-full text-left text-[13px] px-2 py-1.5 rounded hover:bg-brand-50 hover:text-brand-700 cursor-grab active:cursor-grabbing flex items-center gap-2"
            data-action="click->report-builder#addColumn"
            data-tray-item="true"
            data-column-key="${this._escapeAttr(col.key)}">
            <span class="text-gray-300 text-[14px] leading-none" aria-hidden="true">⋮⋮</span>
            <span class="flex-1 truncate">${this._escapeHtml(col.label)}</span>
            <span class="text-[11px] text-gray-400 flex-shrink-0">${col.kind}</span>
          </button>`
        })
        html += `</div></div>`
      }
    }

    this.availableTrayTarget.innerHTML = html ||
      `<p class="text-[13px] text-gray-500 px-2 py-3 text-center">${q ? "No matching columns." : "No columns left to add."}</p>`
  }

  _renderLinkedTray() {
    if (!this.hasLinkedTrayTarget || !this._currentDataset) return
    const links = this._currentDataset.links || []
    if (links.length === 0) {
      this.linkedTrayTarget.innerHTML = ""
      return
    }
    const used = new Set(this._definition.column_keys)
    const q = (this.hasColumnSearchTarget ? this.columnSearchTarget.value : "").trim().toLowerCase()
    const matchesQuery = (label, key) =>
      !q || label.toLowerCase().includes(q) || (key || "").toLowerCase().includes(q)
    let body = ""
    links.forEach(link => {
      const linkRows = link.columns
        .filter(col => !used.has(`${link.key}__${col.key}`))
        .filter(col => matchesQuery(`${link.label} ${col.label}`, `${link.key}__${col.key}`))
      if (linkRows.length === 0) return
      body += `<div class="mb-2">
        <div class="text-[12px] text-gray-700 px-2 font-medium">${this._escapeHtml(link.label)}</div>
        <div class="space-y-1">`
      linkRows.forEach(col => {
        const linkKey = `${link.key}__${col.key}`
        body += `<button type="button"
          class="w-full text-left text-[13px] px-2 py-1.5 rounded hover:bg-brand-50 hover:text-brand-700 cursor-grab active:cursor-grabbing flex items-center gap-2"
          data-action="click->report-builder#addColumn"
          data-tray-item="true"
          data-column-key="${this._escapeAttr(linkKey)}">
          <span class="text-gray-300 text-[14px] leading-none" aria-hidden="true">⋮⋮</span>
          <span class="flex-1 truncate">${this._escapeHtml(col.label)}</span>
          <span class="text-[11px] text-gray-400 flex-shrink-0">${col.data_type}</span>
        </button>`
      })
      body += `</div></div>`
    })
    this.linkedTrayTarget.innerHTML = body
      ? `<div class="text-[11px] font-medium uppercase tracking-wide text-gray-500 px-2 py-1 mt-3">Linked Items</div>${body}`
      : ""
  }

  _categoryFor(dataType) {
    if (["currency", "percent", "number"].includes(dataType)) return "Financial"
    if (["date", "datetime"].includes(dataType)) return "Schedule"
    if (["boolean", "enum"].includes(dataType)) return "Status"
    return "Basic"
  }

  _renderCanvas() {
    if (!this.hasCanvasTarget) return
    const cols = this._definition.column_keys.map(k => this._lookupColumn(k)).filter(Boolean)
    if (cols.length === 0) {
      this.canvasTarget.innerHTML = ""
      if (this.hasCanvasEmptyTarget) this.canvasEmptyTarget.classList.remove("hidden")
      return
    }
    if (this.hasCanvasEmptyTarget) this.canvasEmptyTarget.classList.add("hidden")
    this.canvasTarget.innerHTML = cols.map(col => `
      <div class="flex items-center justify-between gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg cursor-move"
           data-column-key="${this._escapeAttr(col.key)}">
        <div class="flex items-center gap-2 min-w-0">
          <span class="text-gray-400 text-[14px]" aria-label="drag handle">⋮⋮</span>
          <span class="text-[13px] font-medium truncate">${this._escapeHtml(col.label)}</span>
          <span class="text-[11px] text-gray-400 flex-shrink-0">${col.data_type}</span>
        </div>
        <button type="button"
                class="text-gray-400 hover:text-rose-600 text-[16px] leading-none cursor-pointer"
                data-action="click->report-builder#removeColumn"
                data-column-key="${this._escapeAttr(col.key)}">×</button>
      </div>`).join("")
  }

  _renderFilters() {
    if (!this.hasFiltersListTarget) return
    if (!this._currentDataset) {
      this.filtersListTarget.innerHTML = ""
      return
    }
    const filterableCols = this._currentDataset.columns.filter(c => c.filterable)
    const datasetFilters = this._currentDataset.filters || []
    if (this._definition.filters.length === 0) {
      this.filtersListTarget.innerHTML = `<p class="text-[13px] text-gray-500">No filters yet.</p>`
    } else {
      this.filtersListTarget.innerHTML = this._definition.filters.map((f, i) => {
        const col = this._lookupColumn(f.column_key) || filterableCols[0]
        const filterMeta = datasetFilters.find(df => df.key === f.column_key)
        const dataType = filterMeta?.data_type || col?.data_type || "string"
        const operators = this._operatorsFor(dataType)
        const valueInput = this._renderFilterValueInput(f, i, dataType, filterMeta?.options)
        return `<div class="flex items-center gap-1 mb-2">
          <span class="text-[11px] text-gray-400 w-4">${i + 1}</span>
          <select class="text-[13px] border border-gray-200 rounded px-1.5 py-1 bg-white"
                  data-action="change->report-builder#updateFilterColumn"
                  data-filter-index="${i}">
            ${filterableCols.map(c => `<option value="${this._escapeAttr(c.key)}" ${c.key === f.column_key ? "selected" : ""}>${this._escapeHtml(c.label)}</option>`).join("")}
          </select>
          <select class="text-[13px] border border-gray-200 rounded px-1.5 py-1 bg-white"
                  data-action="change->report-builder#updateFilterOperator"
                  data-filter-index="${i}">
            ${operators.map(op => `<option value="${op}" ${op === f.operator ? "selected" : ""}>${this._operatorLabel(op)}</option>`).join("")}
          </select>
          ${valueInput}
          <button type="button"
                  class="text-gray-400 hover:text-rose-600 cursor-pointer text-[14px] px-1"
                  data-action="click->report-builder#removeFilter"
                  data-filter-index="${i}">×</button>
        </div>`
      }).join("")
    }

    // Keep the filter-logic controller's count in sync.
    if (this.hasFilterLogicInputTarget) {
      const logicHost = this.filterLogicInputTarget.closest("[data-controller~='report-filter-logic']")
      if (logicHost) {
        logicHost.dataset.reportFilterLogicFilterCountValue = this._definition.filters.length
      }
    }
    this._updateBadges()
  }

  _renderSort() {
    if (!this.hasSortListTarget) return
    if (!this._currentDataset) {
      this.sortListTarget.innerHTML = ""
      return
    }
    const sortableCols = this._currentDataset.columns.filter(c => c.sortable)
    if (this._definition.sort_by.length === 0) {
      this.sortListTarget.innerHTML = `<p class="text-[13px] text-gray-500">No sorts yet.</p>`
      this._updateBadges()
      return
    }
    this.sortListTarget.innerHTML = this._definition.sort_by.map((s, i) => `
      <div class="flex items-center gap-1 mb-2">
        <select class="text-[13px] border border-gray-200 rounded px-1.5 py-1 bg-white flex-1"
                data-action="change->report-builder#updateSortColumn"
                data-sort-index="${i}">
          ${sortableCols.map(c => `<option value="${this._escapeAttr(c.key)}" ${c.key === s.column_key ? "selected" : ""}>${this._escapeHtml(c.label)}</option>`).join("")}
        </select>
        <select class="text-[13px] border border-gray-200 rounded px-1.5 py-1 bg-white"
                data-action="change->report-builder#updateSortDirection"
                data-sort-index="${i}">
          <option value="asc" ${s.direction === "asc" ? "selected" : ""}>↑ Asc</option>
          <option value="desc" ${s.direction === "desc" ? "selected" : ""}>↓ Desc</option>
        </select>
        <button type="button"
                class="text-gray-400 hover:text-rose-600 cursor-pointer text-[14px] px-1"
                data-action="click->report-builder#removeSort"
                data-sort-index="${i}">×</button>
      </div>`).join("")
    this._updateBadges()
  }

  _renderGroup() {
    if (!this.hasGroupListTarget) return
    if (!this._currentDataset) {
      this.groupListTarget.innerHTML = ""
      return
    }
    const groupable = this._currentDataset.columns.filter(c => c.filterable)
    const items = this._definition.group_by_keys
    let html = ""
    if (items.length === 0) {
      html = `<p class="text-[13px] text-gray-500">No grouping. <button type="button" class="text-brand-600 hover:underline cursor-pointer" data-action="click->report-builder#addGroup">+ Add level</button></p>`
    } else {
      html = items.map((g, i) => `
        <div class="flex items-center gap-1 mb-2">
          <span class="text-[11px] text-gray-400 w-6">L${i + 1}</span>
          <select class="text-[13px] border border-gray-200 rounded px-1.5 py-1 bg-white flex-1"
                  data-action="change->report-builder#updateGroupColumn"
                  data-group-index="${i}">
            ${groupable.map(c => `<option value="${this._escapeAttr(c.key)}" ${c.key === g ? "selected" : ""}>${this._escapeHtml(c.label)}</option>`).join("")}
          </select>
          <button type="button"
                  class="text-gray-400 hover:text-rose-600 cursor-pointer text-[14px] px-1"
                  data-action="click->report-builder#removeGroup"
                  data-group-index="${i}">×</button>
        </div>`).join("")
      if (items.length < 3) {
        html += `<button type="button"
                  class="text-[12px] text-brand-600 hover:underline cursor-pointer"
                  data-action="click->report-builder#addGroup">+ Add level (${items.length}/3)</button>`
      }
    }
    this.groupListTarget.innerHTML = html
    this._updateBadges()
  }

  _renderAggregates() {
    if (!this.hasAggregateListTarget) return
    const aggCols = this._availableAggregateColumns()
    const entries = Object.entries(this._definition.aggregates || {})
    let html = ""
    if (entries.length === 0) {
      html = `<p class="text-[13px] text-gray-500">No aggregates. <button type="button" class="text-brand-600 hover:underline cursor-pointer" data-action="click->report-builder#addAggregate">+ Add</button></p>`
    } else {
      html = entries.map(([colKey, fn]) => {
        const col = this._lookupColumn(colKey) || { label: colKey }
        return `<div class="flex items-center gap-1 mb-2">
          <span class="text-[12px] flex-1 truncate">${this._escapeHtml(col.label)}</span>
          <select class="text-[13px] border border-gray-200 rounded px-1.5 py-1 bg-white"
                  data-action="change->report-builder#updateAggregate"
                  data-aggregate-column-key="${this._escapeAttr(colKey)}">
            <option value="sum" ${fn === "sum" ? "selected" : ""}>Sum</option>
            <option value="count" ${fn === "count" ? "selected" : ""}>Count</option>
            <option value="count_distinct" ${fn === "count_distinct" ? "selected" : ""}>Distinct</option>
            <option value="avg" ${fn === "avg" ? "selected" : ""}>Average</option>
            <option value="min" ${fn === "min" ? "selected" : ""}>Min</option>
            <option value="max" ${fn === "max" ? "selected" : ""}>Max</option>
          </select>
          <button type="button"
                  class="text-gray-400 hover:text-rose-600 cursor-pointer text-[14px] px-1"
                  data-action="click->report-builder#removeAggregate"
                  data-aggregate-column-key="${this._escapeAttr(colKey)}">×</button>
        </div>`
      }).join("")
      html += `<button type="button"
                class="text-[12px] text-brand-600 hover:underline cursor-pointer"
                data-action="click->report-builder#addAggregate">+ Add</button>`
    }
    this.aggregateListTarget.innerHTML = html
    this._updateBadges()
  }

  _renderCharts() {
    if (!this.hasChartListTarget) return
    const blocks = this._definition.chart_blocks || []
    const cols = this._currentDataset?.columns || []
    let html = ""
    if (blocks.length === 0) {
      html = `<p class="text-[13px] text-gray-500">No charts yet. <button type="button" class="text-brand-600 hover:underline cursor-pointer" data-action="click->report-builder#addChartBlock">+ Add</button></p>`
    } else {
      html = blocks.map((b, i) => {
        const groupOptions = cols.map(c =>
          `<option value="${this._escapeAttr(c.key)}" ${c.key === b.group_by_key ? "selected" : ""}>${this._escapeHtml(c.label)}</option>`).join("")
        const valueOptions = cols.map(c =>
          `<option value="${this._escapeAttr(c.key)}" ${c.key === b.value_key ? "selected" : ""}>${this._escapeHtml(c.label)}</option>`).join("")
        return `<div class="border border-gray-200 rounded p-2 mb-2 space-y-1">
          <div class="flex items-center gap-1">
            <select class="text-[13px] border border-gray-200 rounded px-1.5 py-1 bg-white flex-1"
                    data-action="change->report-builder#updateChartField"
                    data-chart-index="${i}" data-field="kind">
              ${[
                ["bar", "Bar"],
                ["line", "Line"],
                ["pie", "Pie"],
                ["stacked_bar", "Stacked Bar"],
                ["sparkline_cell", "Sparkline"]
              ].map(([k, label]) =>
                `<option value="${k}" ${k === b.kind ? "selected" : ""}>${label}</option>`).join("")}
            </select>
            <button type="button"
                    class="text-gray-400 hover:text-rose-600 cursor-pointer text-[14px] px-1"
                    data-action="click->report-builder#removeChartBlock"
                    data-chart-index="${i}">×</button>
          </div>
          <div class="flex items-center gap-1">
            <span class="text-[11px] text-gray-500 w-12">Group</span>
            <select class="text-[13px] border border-gray-200 rounded px-1.5 py-1 bg-white flex-1"
                    data-action="change->report-builder#updateChartField"
                    data-chart-index="${i}" data-field="group_by_key">${groupOptions}</select>
          </div>
          <div class="flex items-center gap-1">
            <span class="text-[11px] text-gray-500 w-12">Value</span>
            <select class="text-[13px] border border-gray-200 rounded px-1.5 py-1 bg-white flex-1"
                    data-action="change->report-builder#updateChartField"
                    data-chart-index="${i}" data-field="value_key">${valueOptions}</select>
            <select class="text-[13px] border border-gray-200 rounded px-1.5 py-1 bg-white"
                    data-action="change->report-builder#updateChartField"
                    data-chart-index="${i}" data-field="value_aggregate">
              ${[
                ["count", "Count"],
                ["sum", "Sum"],
                ["avg", "Average"],
                ["min", "Min"],
                ["max", "Max"]
              ].map(([fn, label]) =>
                `<option value="${fn}" ${fn === b.value_aggregate ? "selected" : ""}>${label}</option>`).join("")}
            </select>
          </div>
          ${b.kind === "stacked_bar" ? `
          <div class="flex items-center gap-1">
            <span class="text-[11px] text-gray-500 w-12">Stack</span>
            <select class="text-[13px] border border-gray-200 rounded px-1.5 py-1 bg-white flex-1"
                    data-action="change->report-builder#updateChartField"
                    data-chart-index="${i}" data-field="stack_by_key">
              <option value="">(none)</option>
              ${cols.map(c => `<option value="${this._escapeAttr(c.key)}" ${c.key === b.stack_by_key ? "selected" : ""}>${this._escapeHtml(c.label)}</option>`).join("")}
            </select>
          </div>` : ""}
        </div>`
      }).join("")
      if (blocks.length < 4) {
        html += `<button type="button"
                  class="text-[12px] text-brand-600 hover:underline cursor-pointer"
                  data-action="click->report-builder#addChartBlock">+ Add (${blocks.length}/4)</button>`
      }
    }
    this.chartListTarget.innerHTML = html
    this._updateBadges()
  }

  _renderFormulas() {
    if (!this.hasFormulaListTarget) return
    const items = this._definition.formula_columns || []
    if (items.length === 0) {
      this.formulaListTarget.innerHTML =
        `<p class="text-[13px] text-gray-500">No formulas or buckets.</p>`
      this._updateBadges()
      return
    }
    this.formulaListTarget.innerHTML = items.map(item => `
      <div class="flex items-center gap-1 mb-1">
        <span class="text-[11px] text-gray-400 uppercase">${this._escapeHtml(item.kind || "formula")}</span>
        <span class="text-[12px] flex-1 truncate">${this._escapeHtml(item.label || item.key)}</span>
        <button type="button"
                class="text-gray-400 hover:text-rose-600 cursor-pointer text-[14px] px-1"
                data-action="click->report-builder#removeFormula"
                data-formula-key="${this._escapeAttr(item.key)}">×</button>
      </div>`).join("")
    this._updateBadges()
  }

  _availableAggregateColumns() {
    if (!this._currentDataset) return []
    const used = new Set(Object.keys(this._definition.aggregates || {}))
    return this._currentDataset.columns.filter(c => c.aggregatable && !used.has(c.key))
  }

  // Type-aware value input — enum with options renders a <select>,
  // boolean renders Yes/No, date/datetime render their native pickers,
  // valueless operators (is_null / is_not_null) render a hint label,
  // everything else falls back to a free-text input.
  _renderFilterValueInput(f, idx, dataType, options) {
    if (f.operator === "is_null" || f.operator === "is_not_null") {
      return `<span class="text-[11px] text-gray-400 italic flex-1">no value needed</span>`
    }
    const baseAttrs = `class="text-[13px] border border-gray-200 rounded px-1.5 py-1 bg-white flex-1 min-w-0"
                       data-action="input->report-builder#updateFilterValue change->report-builder#updateFilterValue"
                       data-filter-index="${idx}"`
    const isMulti = (f.operator === "in" || f.operator === "nin")

    // Enum with options → <select>. For `in`/`nin` we degrade to text
    // (multi-select chip UI is a follow-up).
    if (dataType === "enum" && Array.isArray(options) && options.length > 0 && !isMulti) {
      const opts = [`<option value="">— pick a value —</option>`]
        .concat(options.map(o =>
          `<option value="${this._escapeAttr(o.value)}" ${String(o.value) === String(f.value) ? "selected" : ""}>${this._escapeHtml(o.label)}</option>`
        )).join("")
      return `<select ${baseAttrs} data-action="change->report-builder#updateFilterValue" data-filter-index="${idx}">${opts}</select>`
    }

    if (dataType === "boolean") {
      const opts = [
        `<option value="">— pick a value —</option>`,
        `<option value="true" ${String(f.value) === "true" ? "selected" : ""}>Yes</option>`,
        `<option value="false" ${String(f.value) === "false" ? "selected" : ""}>No</option>`
      ].join("")
      return `<select ${baseAttrs} data-action="change->report-builder#updateFilterValue" data-filter-index="${idx}">${opts}</select>`
    }

    if (dataType === "date") {
      return `<input type="date" ${baseAttrs} value="${this._escapeAttr(f.value || "")}" />`
    }

    if (dataType === "datetime") {
      return `<input type="datetime-local" ${baseAttrs} value="${this._escapeAttr(f.value || "")}" />`
    }

    if (dataType === "number" || dataType === "currency" || dataType === "percent") {
      return `<input type="number" step="any" ${baseAttrs} value="${this._escapeAttr(f.value || "")}" placeholder="value" />`
    }

    const placeholder = isMulti ? "comma-separated values" : "value"
    return `<input type="text" ${baseAttrs} value="${this._escapeAttr(f.value || "")}" placeholder="${placeholder}" />`
  }

  _operatorsFor(dataType) {
    const map = {
      string:   ["eq", "neq", "contains", "starts_with", "is_null", "is_not_null"],
      number:   ["eq", "neq", "lt", "lte", "gt", "gte", "between", "is_null", "is_not_null"],
      currency: ["eq", "neq", "lt", "lte", "gt", "gte", "between", "is_null", "is_not_null"],
      percent:  ["eq", "neq", "lt", "lte", "gt", "gte", "between", "is_null", "is_not_null"],
      date:     ["eq", "neq", "lt", "lte", "gt", "gte", "between", "is_null", "is_not_null"],
      datetime: ["eq", "neq", "lt", "lte", "gt", "gte", "between", "is_null", "is_not_null"],
      boolean:  ["eq", "is_null", "is_not_null"],
      enum:     ["eq", "neq", "in", "nin", "is_null", "is_not_null"],
      array:    ["contains", "is_null", "is_not_null"]
    }
    return map[dataType] || map.string
  }

  _operatorLabel(op) {
    const labels = {
      eq: "=", neq: "≠", lt: "<", lte: "≤", gt: ">", gte: "≥",
      contains: "contains", starts_with: "starts with",
      between: "between", in: "is one of", nin: "is none of",
      is_null: "is empty", is_not_null: "is not empty"
    }
    return labels[op] || op
  }

  _lookupColumn(key) {
    if (!this._currentDataset) return null
    const dsCol = this._currentDataset.columns.find(c => c.key === key)
    if (dsCol) return dsCol
    const virt = (this._definition.formula_columns || []).find(f => f.key === key)
    if (virt) return { key: virt.key, label: virt.label || virt.key, data_type: virt.data_type || (virt.kind === "bucket" ? "enum" : "number"), aggregatable: virt.kind !== "bucket", filterable: false, sortable: true }
    if (key.startsWith("linked_") && key.includes("__")) {
      const sepIdx = key.indexOf("__")
      const linkKey = key.slice(0, sepIdx)
      const colKey = key.slice(sepIdx + 2)
      const link = (this._currentDataset.links || []).find(l => l.key === linkKey)
      if (link) {
        const col = (link.columns || []).find(c => c.key === colKey)
        if (col) return { key, label: `${link.label} · ${col.label}`, data_type: col.data_type, aggregatable: false, filterable: false, sortable: false }
      }
    }
    return null
  }

  _initSortables() {
    // Tray sources — every `[data-tray-item]` row in the available + linked
    // trays becomes a clone-pull drag source sharing the "report-columns"
    // group with the canvas. The source DOM is not reordered (sort: false)
    // and cannot accept drops (put: false).
    if (!this._traySortables) this._traySortables = []
    this._traySortables.forEach(s => s.destroy())
    this._traySortables = []

    const traySources = []
    if (this.hasAvailableTrayTarget) {
      this.availableTrayTarget.querySelectorAll(":scope > div > div.space-y-1").forEach(el => traySources.push(el))
    }
    if (this.hasLinkedTrayTarget) {
      this.linkedTrayTarget.querySelectorAll(":scope > div > div.space-y-1").forEach(el => traySources.push(el))
    }
    const setReceiving = (on) => {
      if (!this.hasCanvasTarget) return
      this.canvasTarget.classList.toggle("report-canvas--receiving", on)
    }

    traySources.forEach(el => {
      this._traySortables.push(Sortable.create(el, {
        group: { name: "report-columns", pull: "clone", put: false },
        sort: false,
        draggable: "[data-tray-item]",
        animation: 150,
        ghostClass: "opacity-40",
        onStart: () => setReceiving(true),
        onEnd:   () => setReceiving(false)
      }))
    })

    if (this.hasCanvasTarget) {
      if (this._canvasSortable) this._canvasSortable.destroy()
      this._canvasSortable = Sortable.create(this.canvasTarget, {
        group: { name: "report-columns", pull: false, put: true },
        animation: 150,
        ghostClass: "bg-brand-50",
        emptyInsertThreshold: 40,
        onAdd: (evt) => {
          // Tray drop — capture the dropped key + index, remove the cloned
          // node (we re-render canvas from `_definition.column_keys`), and
          // splice the key into the right position.
          const key = evt.item?.dataset?.columnKey
          const idx = evt.newIndex
          evt.item.remove()
          if (!key) return
          const existing = this._definition.column_keys.indexOf(key)
          if (existing !== -1) this._definition.column_keys.splice(existing, 1)
          const insertAt = Math.min(Math.max(idx, 0), this._definition.column_keys.length)
          this._definition.column_keys.splice(insertAt, 0, key)
          this._renderCanvas(); this._renderTray(); this._renderLinkedTray()
          this._initSortables()
          this._writeHiddenInputs()
          this._schedulePreview()
        },
        onEnd: (evt) => {
          // Internal reorder only — onAdd handled drops from tray.
          if (evt.from !== evt.to) return
          this._definition.column_keys = Array.from(this.canvasTarget.children)
            .map(el => el.dataset.columnKey).filter(Boolean)
          this._writeHiddenInputs()
          this._schedulePreview()
        }
      })
    }
  }

  _writeHiddenInputs() {
    if (this.hasColumnKeysInputTarget)
      this.columnKeysInputTarget.value = JSON.stringify(this._definition.column_keys)
    if (this.hasFiltersInputTarget)
      this.filtersInputTarget.value = JSON.stringify(this._definition.filters)
    if (this.hasSortByInputTarget)
      this.sortByInputTarget.value = JSON.stringify(this._definition.sort_by)
    if (this.hasGroupByKeysInputTarget)
      this.groupByKeysInputTarget.value = JSON.stringify(this._definition.group_by_keys)
    if (this.hasAggregatesInputTarget)
      this.aggregatesInputTarget.value = JSON.stringify(this._definition.aggregates || {})
    if (this.hasFormulaColumnsInputTarget)
      this.formulaColumnsInputTarget.value = JSON.stringify(this._definition.formula_columns || [])
    if (this.hasChartBlocksInputTarget)
      this.chartBlocksInputTarget.value = JSON.stringify(this._definition.chart_blocks || [])
    if (this.hasFilterLogicInputTarget)
      this.filterLogicInputTarget.value = this._definition.filter_logic || ""
    if (this.hasDatasetKeyInputTarget)
      this.datasetKeyInputTarget.value = this._definition.dataset_key || ""
  }

  _schedulePreview(delay = 600) {
    if (!this.hasPreviewBodyTarget || !this.previewUrlValue) return
    if (this._previewTimer) clearTimeout(this._previewTimer)
    this._previewTimer = setTimeout(() => this._runPreview(), delay)
  }

  async _runPreview() {
    if (!this.previewUrlValue) return
    // No-fetch idle states — render the friendly placeholder instead of
    // hitting the server with an incomplete definition (server returns a
    // red "Preview failed" otherwise, which is alarming on initial load
    // before the user has done anything).
    if (!this._definition.dataset_key || (this._definition.column_keys || []).length === 0) {
      if (this.hasPreviewBodyTarget) {
        this.previewBodyTarget.innerHTML =
          `<p class="text-[13px] text-gray-500 p-4">Add a column to see a preview.</p>`
      }
      if (this.hasPreviewSummaryTarget) this.previewSummaryTarget.textContent = ""
      return
    }
    try {
      const csrf = document.querySelector("meta[name='csrf-token']")?.content
      const resp = await fetch(this.previewUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": csrf || ""
        },
        body: JSON.stringify({ report: this._definition })
      })
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({ error: resp.statusText }))
        this._renderPreviewError(err.error || resp.statusText)
        return
      }
      const json = await resp.json()
      this._renderPreview(json)
      this._renderChartPreviews(json)
    } catch (e) {
      this._renderPreviewError(e.message)
    }
  }

  _renderPreview(payload) {
    const cols = this._definition.column_keys.map(k => this._lookupColumn(k)).filter(Boolean)
    if (cols.length === 0) {
      this.previewBodyTarget.innerHTML = `<p class="text-[13px] text-gray-500 p-4">Add a column to see a preview.</p>`
      return
    }
    const rows = (payload.rows || []).filter(r => !r._group)
    let html = `<table class="w-full text-[13px] border-collapse">`
    html += `<thead class="bg-gray-50 text-gray-500 text-[11px] uppercase tracking-wide"><tr>`
    cols.forEach(c => {
      html += `<th class="px-2 py-1.5 text-left font-medium">${this._escapeHtml(c.label)}</th>`
    })
    html += `</tr></thead><tbody class="divide-y divide-gray-100">`
    rows.forEach(row => {
      html += `<tr>`
      cols.forEach(c => {
        const v = row[c.key]
        html += `<td class="px-2 py-1.5">${this._escapeHtml(this._formatCell(v, c.data_type))}</td>`
      })
      html += `</tr>`
    })
    if (rows.length === 0) {
      html += `<tr><td colspan="${cols.length}" class="px-2 py-4 text-gray-400 text-center">No rows match the current filters.</td></tr>`
    }
    html += `</tbody></table>`
    this.previewBodyTarget.innerHTML = html

    if (this.hasPreviewSummaryTarget) {
      const truncated = payload.truncated ? "+ more" : ""
      this.previewSummaryTarget.textContent =
        `${payload.row_count} row${payload.row_count === 1 ? "" : "s"} ${truncated} • ${payload.elapsed_ms || 0} ms`
    }
  }

  _renderChartPreviews(payload) {
    if (!this.hasChartPreviewAreaTarget) return
    const charts = payload.chart_data || {}
    const blocks = this._definition.chart_blocks || []
    if (blocks.length === 0) {
      this.chartPreviewAreaTarget.innerHTML = ""
      return
    }
    const kindLabels = {
      bar: "Bar", line: "Line", pie: "Pie",
      stacked_bar: "Stacked Bar", sparkline_cell: "Sparkline"
    }
    this.chartPreviewAreaTarget.innerHTML = blocks.map((b, i) => {
      const id = `chart_${i}`
      const data = charts[id] || { labels: [], datasets: [] }
      const kind = b.kind || "bar"
      return `<div class="bg-white border border-gray-200 rounded-lg p-2 mb-2"
                   data-controller="report-chart-block"
                   data-report-chart-block-kind-value="${this._escapeAttr(kind)}"
                   data-report-chart-block-chart-data-value='${this._escapeAttr(JSON.stringify(data))}'>
        <div class="text-[11px] uppercase tracking-wide text-gray-500 mb-1">${this._escapeHtml(kindLabels[kind] || kind)}</div>
        <div class="relative" style="height: 220px">
          <canvas data-report-chart-block-target="canvas"></canvas>
        </div>
      </div>`
    }).join("")
  }

  _renderPreviewError(message) {
    this.previewBodyTarget.innerHTML =
      `<p class="text-[13px] text-rose-600 p-4">Preview failed: ${this._escapeHtml(message)}</p>`
  }

  _formatCell(value, dataType) {
    if (value == null || value === "") return ""
    if (dataType === "currency") {
      const num = Number(value)
      if (Number.isFinite(num)) return num.toLocaleString(undefined, { style: "currency", currency: "USD" })
    }
    if (dataType === "percent") {
      const num = Number(value)
      if (Number.isFinite(num)) return `${num.toFixed(1)}%`
    }
    if (dataType === "boolean") return value ? "Yes" : "No"
    if (Array.isArray(value)) return value.join(", ")
    return String(value)
  }

  _loadCollapsedState() {
    try {
      const stored = JSON.parse(window.localStorage.getItem("reportBuilderCollapsed") || "{}")
      return stored && typeof stored === "object" ? stored : {}
    } catch (e) { return {} }
  }

  _saveCollapsedState() {
    try {
      window.localStorage.setItem("reportBuilderCollapsed", JSON.stringify(this._collapsed))
    } catch (e) { /* swallow */ }
  }

  _applyAccordionState() {
    document.querySelectorAll("[data-builder-section]").forEach(section => {
      const name = section.dataset.builderSection
      const body = section.querySelector("[data-builder-body]")
      const chevron = section.querySelector("[data-builder-chevron]")
      if (!body) return
      const collapsed = !!this._collapsed[name]
      body.classList.toggle("hidden", collapsed)
      if (chevron) chevron.textContent = collapsed ? "▸" : "▾"
    })
  }

  _escapeHtml(s) {
    return String(s ?? "").replace(/[&<>"']/g, m => ({
      "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;"
    }[m]))
  }

  _escapeAttr(s) {
    return this._escapeHtml(s)
  }
}
